import {
	type CustomQuestion,
	type DocumentItem,
	type FormConfigV1,
	clampMinLivingHistoryYears,
	fieldEnabled,
	prebuiltQuestionEnabled,
	sectionEnabled,
} from '#app/utils/application-form/config'

export type ContactFieldKey = 'phone' | 'email'
export type EmployerContactFieldKey = 'phone' | 'work_address'

/** Landlord contact fields that are on for this config. */
export function enabledLandlordContactFields(
	config: FormConfigV1,
): ContactFieldKey[] {
	if (!sectionEnabled(config, 'landlord_contact')) return []
	return (['phone', 'email'] as const).filter((key) =>
		fieldEnabled(config, 'landlord_contact', key),
	)
}

/** Employer contact chips that are on (phone, work address). */
export function enabledEmployerContactFields(
	config: FormConfigV1,
): EmployerContactFieldKey[] {
	if (!sectionEnabled(config, 'employer_contact')) return []
	return (['phone', 'work_address'] as const).filter((key) =>
		fieldEnabled(config, 'employer_contact', key),
	)
}

export function isLandlordContactFieldOn(
	config: FormConfigV1,
	field: ContactFieldKey,
): boolean {
	return enabledLandlordContactFields(config).includes(field)
}

export function isEmployerContactFieldOn(
	config: FormConfigV1,
	field: EmployerContactFieldKey,
): boolean {
	return enabledEmployerContactFields(config).includes(field)
}

/** Enabled custom questions for a question block (enabled !== false). */
export function enabledCustomQuestions(
	config: FormConfigV1,
	block: 'rental_questions' | 'employment_questions',
): CustomQuestion[] {
	const section = config.sections[block]
	if (!section.enabled) return []
	return (section.custom ?? []).filter((question) => question.enabled !== false)
}

/** Prebuilt question keys that are on for a block. */
export function enabledPrebuiltQuestionKeys(
	config: FormConfigV1,
	block: 'rental_questions' | 'employment_questions',
): string[] {
	const section = config.sections[block]
	if (!section.enabled) return []
	return Object.entries(section.prebuilt)
		.filter(([, on]) => on)
		.map(([key]) => key)
}

export function isPrebuiltQuestionOn(
	config: FormConfigV1,
	block: 'rental_questions' | 'employment_questions',
	key: string,
): boolean {
	return prebuiltQuestionEnabled(config, block, key)
}

/**
 * Documents section for Review.
 * When disabled → null (hide).
 * When enabled → configured items (may be empty list).
 */
export function reviewDocumentItems(
	config: FormConfigV1,
): DocumentItem[] | null {
	if (!sectionEnabled(config, 'documents')) return null
	return (config.sections.documents.items ?? []).map((item) => ({ ...item }))
}

/** Seed Review document rows from config + any already-saved uploads.
 * `requested` rows are landlord document requests (requestDocuments intent):
 * always mandatory, and folded in even when the template's documents section
 * is OFF — a request can differ from the pinned template version. */
export function seedReviewDocuments(input: {
	config: FormConfigV1
	saved?: Array<{
		id: string
		name: string
		documentId?: string
		fileName?: string
		blobUrl?: string
	}>
	requested?: Array<{ id: string; name: string }>
}): Array<{
	id: string
	name: string
	documentId?: string
	fileName?: string
	blobUrl?: string
	mandatory?: boolean
}> {
	const configItems = reviewDocumentItems(input.config)
	const requestedItems = (input.requested ?? [])
		.filter((row) => row.name.trim())
		.map((row) => ({ id: row.id, name: row.name, mandatory: true }))
	// Section off AND nothing requested → the legacy empty seed.
	if (configItems === null && requestedItems.length === 0) return []
	// A template row of the same name keeps its slot but the landlord's
	// request upgrades it to mandatory — "requested" always means required.
	const matchesRequested = (item: { id: string; name: string }) =>
		requestedItems.some(
			(row) =>
				row.id === item.id ||
				row.name.trim().toLowerCase() === item.name.trim().toLowerCase(),
		)
	const upgradedConfig = (configItems ?? []).map((item) =>
		matchesRequested(item) ? { ...item, mandatory: true } : item,
	)
	const dedupedRequested = requestedItems.filter(
		(row) =>
			!(configItems ?? []).some(
				(item) =>
					item.id === row.id ||
					item.name.trim().toLowerCase() === row.name.trim().toLowerCase(),
			),
	)
	const items = [...upgradedConfig, ...dedupedRequested]

	const savedById = new Map((input.saved ?? []).map((row) => [row.id, row]))
	const savedByName = new Map(
		(input.saved ?? [])
			.filter((row) => row.name.trim())
			.map((row) => [row.name.trim().toLowerCase(), row]),
	)

	if (items.length === 0) {
		return (input.saved ?? []).map((row) => ({ ...row }))
	}

	const seeded: Array<{
		id: string
		name: string
		documentId?: string
		fileName?: string
		blobUrl?: string
		mandatory?: boolean
	}> = items.map((item) => {
		const match =
			savedById.get(item.id) ?? savedByName.get(item.name.toLowerCase())
		return {
			id: item.id,
			name: item.name,
			mandatory: item.mandatory,
			documentId: match?.documentId,
			fileName: match?.fileName,
			blobUrl: match?.blobUrl,
		}
	})

	// Keep extra saved docs that are not in the template list.
	for (const row of input.saved ?? []) {
		if (seeded.some((item) => item.id === row.id)) continue
		if (
			row.name.trim() &&
			seeded.some(
				(item) =>
					item.name.trim().toLowerCase() === row.name.trim().toLowerCase(),
			)
		) {
			continue
		}
		seeded.push({ ...row, mandatory: false })
	}

	return seeded
}

export function missingMandatoryDocuments(
	docs: Array<{
		name: string
		documentId?: string
		fileName?: string
		mandatory?: boolean
	}>,
): string[] {
	return docs
		.filter(
			(doc) =>
				doc.mandatory && !(doc.documentId || doc.fileName) && doc.name.trim(),
		)
		.map((doc) => doc.name)
}

/**
 * Tolerant reader for stored custom-question answers. Accepts the proper
 * object shape or a legacy double-encoded JSON string (rows written before
 * the screening action parsed this key); anything else yields {}.
 */
export function parseCustomQuestionAnswers(
	value: unknown,
): Record<string, { value: string; explanation?: string }> {
	let raw = value
	if (typeof raw === 'string') {
		try {
			raw = JSON.parse(raw)
		} catch {
			return {}
		}
	}
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
	const answers: Record<string, { value: string; explanation?: string }> = {}
	for (const [id, entry] of Object.entries(raw)) {
		if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue
		const answer = entry as { value?: unknown; explanation?: unknown }
		if (typeof answer.value !== 'string') continue
		answers[id] = {
			value: answer.value,
			...(typeof answer.explanation === 'string'
				? { explanation: answer.explanation }
				: {}),
		}
	}
	return answers
}

export function validateCustomQuestionAnswers(
	questions: CustomQuestion[],
	answers: Record<string, { value: string; explanation?: string }>,
): Record<string, string[]> {
	const errors: Record<string, string[]> = {}
	for (const question of questions) {
		if (question.type === 'dropdown' && question.dropdowns?.length) {
			for (const dropdown of question.dropdowns) {
				const fieldId = `${question.id}::${dropdown.id}`
				const value =
					answers[fieldId]?.value ??
					(dropdown.id === question.id ? answers[question.id]?.value : '') ??
					''
				if (!String(value).trim()) {
					errors[fieldId] = ['Please answer this question']
				}
			}
			continue
		}
		const value = answers[question.id]?.value ?? ''
		if (!String(value).trim()) {
			errors[question.id] = ['Please answer this question']
		} else if (
			question.type === 'yes_no_explanation' &&
			value === 'yes' &&
			!String(answers[question.id]?.explanation ?? '').trim()
		) {
			errors[question.id] = ['Please provide details']
		}
	}
	return errors
}

export function minLivingHistoryYears(config: FormConfigV1): number {
	const section = config.sections.min_living_history
	const configured = section?.years
	if (configured != null && Number.isFinite(configured) && configured >= 1) {
		return clampMinLivingHistoryYears(configured)
	}
	// Toggle off (or legacy null/0) → 1 year minimum by default.
	return 1
}

export function meetsMinLivingHistory(
	config: FormConfigV1,
	years: number,
	months = 0,
): boolean {
	const required = minLivingHistoryYears(config)
	const totalYears = years + months / 12
	return totalYears + 1e-9 >= required
}

/** Employer contact section + chip flags for the employment step. */
export function employerContactUiFlags(config: FormConfigV1): {
	sectionOn: boolean
	phone: boolean
	workAddress: boolean
} {
	const sectionOn = sectionEnabled(config, 'employer_contact')
	if (!sectionOn) {
		return {
			sectionOn: false,
			phone: false,
			workAddress: false,
		}
	}
	return {
		sectionOn: true,
		phone: fieldEnabled(config, 'employer_contact', 'phone'),
		workAddress: fieldEnabled(config, 'employer_contact', 'work_address'),
	}
}

/** Student Occupant Details chips (Gender / School / Field / Level / Graduation). */
export function studentOccupantFieldFlags(config: FormConfigV1): {
	sectionOn: boolean
	gender: boolean
	school: boolean
	fieldOfStudy: boolean
	levelOfStudy: boolean
	expectedGraduation: boolean
} {
	const sectionOn = sectionEnabled(config, 'occupant')
	if (!sectionOn) {
		return {
			sectionOn: false,
			gender: false,
			school: false,
			fieldOfStudy: false,
			levelOfStudy: false,
			expectedGraduation: false,
		}
	}
	return {
		sectionOn: true,
		gender: fieldEnabled(config, 'occupant', 'gender'),
		school: fieldEnabled(config, 'occupant', 'school'),
		fieldOfStudy: fieldEnabled(config, 'occupant', 'field_of_study'),
		levelOfStudy: fieldEnabled(config, 'occupant', 'level_of_study'),
		expectedGraduation: fieldEnabled(config, 'occupant', 'expected_graduation'),
	}
}
